import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

/** Mindest- und Sollbestand eines Technikers für eine Artikelvorlage. */
export interface TechnicianStockLevel {
  id: number;
  user_id: number;
  user_name: string;
  address_state?: string | null;
  article_template_id: number;
  article_template_description: string;
  min_quantity: number;
  target_quantity: number;
}

/** Neue Zeile, wie sie der Dialog zusammenstellt (noch ohne ID). */
export interface TechnicianStockLevelDraft {
  user_id: number;
  article_template_id: number;
  min_quantity: number;
  target_quantity: number;
}

/**
 * Ergebnis des Anlege-Dialogs: die Zeile plus ihre Beschriftung.
 *
 * Beim Anlegen eines neuen Technikers bzw. einer neuen Vorlage existiert die
 * Gegenseite in der Tabelle noch nicht — ohne die mitgelieferte Beschriftung
 * stünde dort nur eine ID.
 */
export interface TechnicianStockLevelSelection {
  draft: TechnicianStockLevelDraft;
  label: string;
  addressState?: string | null;
}

/** Durchschnittswerte anderer Techniker als Vorbelegung, je Artikelvorlage. */
export interface StockLevelAverage {
  min_quantity: number;
  target_quantity: number;
  technician_count: number;
}

/** Eintrag der Auswahlliste — bereits definierte Einträge kommen ausgegraut mit. */
export interface SelectableTemplate {
  id: number;
  description: string;
  comment3?: string | null;
  already_defined: boolean;
}

export interface SelectableTechnician {
  id: number;
  name: string;
  address_state?: string | null;
  already_defined: boolean;
}

export interface TechnicianPage extends PaginatedResult<SelectableTechnician> {
  /**
   * Der tatsächlich angewandte Projektfilter. Ohne eigene Angabe leitet ihn das
   * Backend aus der Artikelvorlage ab (comment2, sonst die Projekte der Kunden
   * aus comment3) — der Dialog zeigt ihn damit als Vorauswahl an, statt eine
   * gefilterte Liste ohne erkennbaren Grund zu präsentieren.
   */
  applied_project_ids: number[];
}

export interface PaginatedResult<T> {
  entries: T[];
  total_count: number;
  page: number;
  per_page: number;
  total_pages: number;
}

@Injectable({ providedIn: 'root' })
export class TechnicianStockLevelService {
  private http = inject(HttpClient);
  private base = `${environment.apiURL}technician_stock_levels`;

  /** Zeilen eines Technikers ODER einer Artikelvorlage — dieselben Daten, zwei Sichten. */
  public async list(filter: { userId?: number; articleTemplateId?: number }): Promise<TechnicianStockLevel[]> {
    let params = new HttpParams();
    if (filter.userId != null) params = params.set('user_id', filter.userId);
    if (filter.articleTemplateId != null) params = params.set('article_template_id', filter.articleTemplateId);

    return firstValueFrom(
      this.http.get<TechnicianStockLevel[]>(`${this.base}.json`, { params, withCredentials: true })
    );
  }

  /** Legt mehrere Zeilen auf einmal an — alles oder nichts. */
  public async createMany(levels: TechnicianStockLevelDraft[]): Promise<TechnicianStockLevel[]> {
    const response = await firstValueFrom(
      this.http.post<{ success: boolean; levels: TechnicianStockLevel[]; error?: string }>(
        `${this.base}.json`, { levels }, { withCredentials: true }
      )
    );
    if (!response.success) throw new Error(response.error || 'Bestände konnten nicht gespeichert werden.');
    return response.levels;
  }

  public async update(id: number, minQuantity: number, targetQuantity: number): Promise<TechnicianStockLevel> {
    const response = await firstValueFrom(
      this.http.patch<{ success: boolean; level: TechnicianStockLevel; error?: string }>(
        `${this.base}/${id}.json`, { min_quantity: minQuantity, target_quantity: targetQuantity },
        { withCredentials: true }
      )
    );
    if (!response.success) throw new Error(response.error || 'Bestand konnte nicht gespeichert werden.');
    return response.level;
  }

  public async remove(id: number): Promise<void> {
    await firstValueFrom(this.http.delete(`${this.base}/${id}.json`, { withCredentials: true }));
  }

  /**
   * Durchschnittswerte anderer Techniker zur selben Vorlage.
   *
   * Ohne addressState über alle Techniker, mit über die des Bundeslands: Ein
   * Techniker in Bayern führt anderes Material als einer in Schleswig-Holstein.
   * Vorlagen ohne Vergleichswerte fehlen im Ergebnis — der Dialog trägt dort 0
   * ein, was ungültig ist und eine bewusste Eingabe erzwingt.
   */
  public async averages(templateIds: number[], addressState?: string | null):
    Promise<Record<number, StockLevelAverage>> {
    if (templateIds.length === 0) return {};

    let params = new HttpParams();
    templateIds.forEach(id => { params = params.append('article_template_ids[]', id); });
    if (addressState) params = params.set('address_state', addressState);

    return firstValueFrom(
      this.http.get<Record<number, StockLevelAverage>>(`${this.base}/averages.json`,
        { params, withCredentials: true })
    );
  }

  /** Vorlagen-Auswahl für den Techniker-Dialog (serverseitig gesucht und paginiert). */
  public async selectableTemplates(options: {
    userId?: number; query?: string; customerId?: number | null; page?: number; perPage?: number;
  }): Promise<PaginatedResult<SelectableTemplate>> {
    let params = new HttpParams().set('page', options.page ?? 1).set('per_page', options.perPage ?? 25);
    if (options.userId != null) params = params.set('user_id', options.userId);
    if (options.query) params = params.set('q', options.query);
    if (options.customerId != null) params = params.set('customer_id', options.customerId);

    return firstValueFrom(
      this.http.get<PaginatedResult<SelectableTemplate>>(`${this.base}/selectable_templates.json`,
        { params, withCredentials: true })
    );
  }

  /** Techniker-Auswahl für den Vorlagen-Dialog. */
  /**
   * `useDefaultProjects` steuert, ob das Backend seine Projekt-Vorauswahl
   * anwenden darf. Nötig, weil eine leere Projektliste im Request nicht von
   * "kein Parameter" zu unterscheiden ist: Ohne das Flag käme die Vorauswahl
   * zurück, sobald der Lagerist alle Projekte abwählt.
   */
  public async selectableTechnicians(options: {
    articleTemplateId?: number; query?: string; projectIds?: number[];
    useDefaultProjects?: boolean; comment2?: string | null; comment3?: string | null;
    page?: number; perPage?: number;
  }): Promise<TechnicianPage> {
    let params = new HttpParams().set('page', options.page ?? 1).set('per_page', options.perPage ?? 25);
    if (options.articleTemplateId != null) params = params.set('article_template_id', options.articleTemplateId);
    if (options.query) params = params.set('q', options.query);
    if (options.useDefaultProjects) params = params.set('default_projects', 'true');
    // Beim Anlegen gibt es noch keine Vorlage — dann liefern die Rohwerte aus
    // dem Formular die Vorauswahl, ausgewertet nach derselben Regel.
    if (options.comment2) params = params.set('comment2', options.comment2);
    if (options.comment3) params = params.set('comment3', options.comment3);
    (options.projectIds ?? []).forEach(id => { params = params.append('project_ids[]', id); });

    return firstValueFrom(
      this.http.get<TechnicianPage>(`${this.base}/selectable_technicians.json`,
        { params, withCredentials: true })
    );
  }
}
